// src/context/KioskContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const KioskContext = createContext();

export const useKiosk = () => {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error('useKiosk must be used within KioskProvider');
  }
  return context;
};

export const KioskProvider = ({ children }) => {
  const [currentOrder, setCurrentOrder] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [menuItems, setMenuItems] = useState([]);

  // Initialize with enhanced healthy menu
  useEffect(() => {
    setMenuItems([
      {
        id: 1,
        name: "Rainbow Buddha Bowl",
        price: 12.99,
        description: "Nutrient-packed quinoa bowl with roasted sweet potato, purple cabbage, edamame, and tahini dressing",
        category: "healthy",
        dietary: ["vegan", "gluten-free"],
        calories: 420,
        protein: 15,
        fiber: 12,
        image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop&auto=format"
      },
      {
        id: 2,
        name: "Mediterranean Power Salad",
        price: 11.99,
        description: "Fresh spinach, chickpeas, cherry tomatoes, cucumber, olives, and lemon-herb vinaigrette",
        category: "salads",
        dietary: ["vegetarian", "gluten-free"],
        calories: 380,
        protein: 14,
        fiber: 11,
        image: "https://images.unsplash.com/photo-1540420773420-3366772f4999?w=400&h=300&fit=crop&auto=format"
      },
      {
        id: 3,
        name: "Green Goddess Bowl",
        price: 13.49,
        description: "Kale, avocado, broccoli, hemp seeds, and creamy green goddess dressing",
        category: "superfood",
        dietary: ["vegan", "raw"],
        calories: 350,
        protein: 12,
        fiber: 15,
        image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=300&fit=crop&auto=format"
      },
      {
        id: 4,
        name: "Protein Paradise",
        price: 15.99,
        description: "Grilled chicken breast, quinoa, roasted vegetables, and turmeric-ginger dressing",
        category: "protein",
        dietary: ["gluten-free", "high-protein"],
        calories: 480,
        protein: 35,
        fiber: 8,
        image: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop&auto=format"
      },
      {
        id: 5,
        name: "Antioxidant Berry Bowl",
        price: 10.99,
        description: "Mixed berries, nuts, seeds, coconut flakes over Greek yogurt with honey drizzle",
        category: "breakfast",
        dietary: ["vegetarian", "probiotic"],
        calories: 320,
        protein: 18,
        fiber: 9,
        image: "https://images.unsplash.com/photo-1511690743698-d9d85f2fbf38?w=400&h=300&fit=crop&auto=format"
      },
      {
        id: 6,
        name: "Omega-3 Salmon Salad",
        price: 16.99,
        description: "Wild-caught salmon, mixed greens, walnuts, and pomegranate vinaigrette",
        category: "seafood",
        dietary: ["gluten-free", "omega-3"],
        calories: 520,
        protein: 32,
        fiber: 6,
        image: "https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=400&h=300&fit=crop&auto=format"
      },
      {
        id: 7,
        name: "Plant Power Wrap",
        price: 9.99,
        description: "Hummus, roasted vegetables, sprouts, and avocado in a whole wheat wrap",
        category: "wraps",
        dietary: ["vegan", "high-fiber"],
        calories: 390,
        protein: 16,
        fiber: 14,
        image: "https://images.unsplash.com/photo-1551782450-17144efb9c50?w=400&h=300&fit=crop&auto=format"
      },
      {
        id: 8,
        name: "Superfood Smoothie Bowl",
        price: 11.49,
        description: "Acai, banana, spinach, chia seeds, topped with granola and fresh fruit",
        category: "smoothies",
        dietary: ["vegan", "antioxidant-rich"],
        calories: 340,
        protein: 10,
        fiber: 13,
        image: "https://images.unsplash.com/photo-1511690743698-d9d85f2fbf38?w=400&h=300&fit=crop&auto=format"
      }
    ]);
  }, []);

  // Dual backend AI system - Pi + Cloud
  const askAI = async (prompt) => {
    try {
      // First try local Pi for simple queries
      if (isSimpleQuery(prompt)) {
        const response = await axios.post('http://localhost:11435/api/local/simple', {
          prompt: prompt,
          menu: menuItems
        }, { timeout: 3000 });
        
        if (response.data && response.data.response) {
          setAiResponse(response.data.response);
          return response.data.response;
        }
      }
      
      // Fallback to cloud GPT-OSS for complex queries
      const response = await axios.post('http://localhost:11434/api/generate', {
        model: 'gpt-oss:20b',
        prompt: `You are HeySalad's intelligent restaurant assistant. You help customers make healthy food choices.

Available HeySalad Menu:
${JSON.stringify(menuItems.map(item => ({
  name: item.name,
  price: item.price,
  description: item.description,
  dietary: item.dietary,
  calories: item.calories,
  protein: item.protein,
  fiber: item.fiber
})), null, 2)}

Customer request: "${prompt}"

Respond naturally and helpfully. Recommend specific menu items with clear reasoning focusing on health benefits. Keep responses friendly and concise (2-3 sentences max).`,
        stream: false
      });

      const aiResponse = response.data.response;
      setAiResponse(aiResponse);
      return aiResponse;
    } catch (error) {
      console.error('AI API Error:', error);
      // Intelligent fallback responses based on prompt content
      const fallback = generateFallbackResponse(prompt);
      setAiResponse(fallback);
      return fallback;
    }
  };

  // Helper function to determine if query is simple
  const isSimpleQuery = (prompt) => {
    const simpleKeywords = ['menu', 'options', 'what', 'show', 'list', 'help', 'hi', 'hello'];
    return simpleKeywords.some(keyword => 
      prompt.toLowerCase().includes(keyword) && prompt.length < 50
    );
  };

  // Generate smart fallback responses
  const generateFallbackResponse = (prompt) => {
    const lowerPrompt = prompt.toLowerCase();
    
    if (lowerPrompt.includes('healthy') || lowerPrompt.includes('nutrition')) {
      return "I'd recommend our Rainbow Buddha Bowl or Green Goddess Bowl - both are packed with nutrients, fiber, and plant-based protein! They're our most popular healthy options.";
    }
    
    if (lowerPrompt.includes('protein') || lowerPrompt.includes('muscle')) {
      return "For high protein, try our Protein Paradise with 35g of protein or the Omega-3 Salmon Salad with 32g! Both support muscle health beautifully.";
    }
    
    if (lowerPrompt.includes('vegan') || lowerPrompt.includes('plant')) {
      return "Our vegan options include the Rainbow Buddha Bowl, Green Goddess Bowl, and Plant Power Wrap - all delicious and nutrient-dense!";
    }
    
    if (lowerPrompt.includes('weight') || lowerPrompt.includes('calories')) {
      return "For lighter options, try our Antioxidant Berry Bowl (320 cal) or Superfood Smoothie Bowl (340 cal) - both satisfying and nutritious!";
    }
    
    return "All our menu items are designed for health and taste! The Mediterranean Power Salad and Rainbow Buddha Bowl are customer favorites. What type of flavors do you enjoy?";
  };

  // Enhanced voice input with better error handling
  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      // Fallback for browsers without speech recognition
      setIsListening(true);
      setTimeout(() => {
        const sampleInput = "I want something healthy and filling";
        askAI(sampleInput);
        setIsListening(false);
      }, 2000);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const voiceInput = event.results[0][0].transcript;
      console.log('Voice input:', voiceInput);
      askAI(voiceInput);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      
      // Provide user-friendly error handling
      if (event.error === 'no-speech') {
        askAI("I didn't catch that - could you try again or browse our menu?");
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    try {
      recognition.start();
    } catch (error) {
      console.error('Recognition start error:', error);
      setIsListening(false);
    }
  };

  const addToOrder = (item) => {
    setCurrentOrder(prev => [...prev, { ...item, orderId: Date.now() }]);
  };

  const removeFromOrder = (orderId) => {
    setCurrentOrder(prev => prev.filter(item => item.orderId !== orderId));
  };

  const clearOrder = () => {
    setCurrentOrder([]);
    setAiResponse('');
  };

  const getTotalPrice = () => {
    return currentOrder.reduce((total, item) => total + item.price, 0).toFixed(2);
  };

  const getTotalCalories = () => {
    return currentOrder.reduce((total, item) => total + item.calories, 0);
  };

  const getTotalProtein = () => {
    return currentOrder.reduce((total, item) => total + (item.protein || 0), 0);
  };

  const getTotalFiber = () => {
    return currentOrder.reduce((total, item) => total + (item.fiber || 0), 0);
  };

  const value = {
    // State
    currentOrder,
    isListening,
    aiResponse,
    menuItems,
    
    // Actions
    askAI,
    startVoiceInput,
    addToOrder,
    removeFromOrder,
    clearOrder,
    getTotalPrice,
    getTotalCalories,
    getTotalProtein,
    getTotalFiber,
    
    // Setters
    setAiResponse,
    setCurrentOrder,
    setMenuItems
  };

  return (
    <KioskContext.Provider value={value}>
      {children}
    </KioskContext.Provider>
  );
};